import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Stack,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SellIcon from '@mui/icons-material/Sell';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getStocks, StockDto, SaleDto, createSaleBySymbol, updateSale, deleteSale } from '../../services/stocksApi';
import SaleForm, { SaleFormValues } from './SaleForm';
import StockSelectionDialog from './StockSelectionDialog';
import { formatEuro, formatQuantity, formatCryptoQuantity } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { InvestmentType } from '../../types/stocks';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

interface SaleWithStock extends SaleDto {
  stockSymbol: string;
  stockPublicId: string;
}

export default function SalesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [sales, setSales] = useState<SaleWithStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saleFormOpen, setSaleFormOpen] = useState(false);
  const [editingSale, setEditingSale] = useState<SaleWithStock | null>(null);
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [stockSelectionOpen, setStockSelectionOpen] = useState(false);
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());

  const fetchStocks = async () => {
    setLoading(true);
    setError('');
    try {
      const stocksData = await getStocks();
      setStocks(stocksData);
      
      // Flatten all sales from all stocks
      const allSales: SaleWithStock[] = [];
      stocksData.forEach(stock => {
        if (stock.sales) {
          stock.sales.forEach(sale => {
            allSales.push({
              ...sale,
              stockSymbol: stock.symbol,
              stockPublicId: stock.publicId
            });
          });
        }
      });
      
      // Sort by date (newest first)
      allSales.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setSales(allSales);
    } catch (err: any) {
      setError(err.message || 'Não foi possível carregar os dados das vendas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
    
    // Check if a stock is pre-selected from URL
    const stockParam = searchParams.get('stock');
    if (stockParam) {
      setSelectedStock(stockParam);
      setStockSelectionOpen(true);
      // Clear the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const getAvailableShares = (stockSymbol: string): number => {
    const stock = stocks.find(s => s.symbol === stockSymbol);
    if (!stock) return 0;
    
    const totalPurchased = stock.purchases?.reduce((sum, p) => sum + p.quantity, 0) || 0;
    const totalSold = stock.sales?.reduce((sum, s) => sum + s.quantity, 0) || 0;
    
    return totalPurchased - totalSold;
  };

  const handleAddSale = () => {
    setEditingSale(null);
    setSelectedStock('');
    setStockSelectionOpen(true);
  };

  const handleStockSelected = () => {
    setStockSelectionOpen(false);
    setSaleFormOpen(true);
  };

  const handleEditSale = (sale: SaleWithStock) => {
    setEditingSale(sale);
    setSelectedStock(sale.stockSymbol);
    setSaleFormOpen(true);
  };

  const handleSaleFormSubmit = async (values: SaleFormValues) => {
    try {
      if (editingSale) {
        // Update existing sale
        await updateSale(editingSale.publicId, {
          date: values.date,
          quantity: Number(values.quantity),
          pricePerShare: Number(values.pricePerShare),
        });
      } else {
        // Create new sale
        if (!selectedStock) {
          setError('Selecione uma ação');
          return;
        }
        await createSaleBySymbol(selectedStock, {
          date: values.date,
          quantity: Number(values.quantity),
          pricePerShare: Number(values.pricePerShare),
        });
      }
      
      setSaleFormOpen(false);
      setEditingSale(null);
      setSelectedStock('');
      fetchStocks(); // Refresh data
    } catch (err: any) {
      setError(err.message || 'Não foi possível guardar a venda');
    }
  };

  const handleDeleteSale = async (sale: SaleWithStock) => {
    if (!window.confirm(`Tem a certeza de que quer eliminar esta venda de ${formatQuantity(sale.quantity)} unidades de ${sale.stockSymbol}?`)) {
      return;
    }
    
    try {
      await deleteSale(sale.publicId);
      fetchStocks(); // Refresh data
    } catch (err: any) {
      setError(err.message || 'Não foi possível eliminar a venda');
    }
  };

  const formatCurrency = (amount: number) => formatEuro(amount);

  const isCryptoBySymbol = (symbol: string | undefined): boolean => {
    if (!symbol) return false;
    const s = stocks.find(x => x.symbol === symbol);
    return s?.investmentType === InvestmentType.Cryptocurrency;
  };

  const toggleSaleExpansion = (saleId: string) => {
    const newExpanded = new Set(expandedSales);
    if (newExpanded.has(saleId)) {
      newExpanded.delete(saleId);
    } else {
      newExpanded.add(saleId);
    }
    setExpandedSales(newExpanded);
  };

  const getPurchaseDetails = (sale: SaleWithStock) => {
    const stock = stocks.find(s => s.symbol === sale.stockSymbol);
    if (!stock || !sale.salePurchases) return [];

    return sale.salePurchases.map(sp => {
      const purchase = stock.purchases?.find(p => p.publicId === sp.purchasePublicId);
      return {
        ...sp,
        purchaseDate: purchase?.date,
        purchasePrice: purchase?.pricePerShare,
        purchaseCambio: undefined
      };
    });
  };

  // Calculate summary statistics
  const totalSalesValue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
  const totalSharesSold = sales.reduce((sum, sale) => sum + sale.quantity, 0);
  const totalGainLoss = sales.reduce((sum, sale) => sum + sale.gainLoss, 0);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" height={40} width={200} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Vendas de ações"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSale}
            color="error"
          >
            Registar venda
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <AutoGrid min={150} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <SellIcon color="error" />
              <Box>
                <Typography variant="h6" component="div">
                  {sales.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de vendas
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <SellIcon color="error" />
              <Box>
                <Typography variant="h6" component="div">
                  {formatQuantity(totalSharesSold)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unidades vendidas
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <SellIcon color="error" />
              <Box>
                <Typography variant="h6" component="div">
                  {formatCurrency(totalSalesValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Valor total
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <SellIcon color={totalGainLoss >= 0 ? "success" : "error"} />
              <Box>
                <Typography 
                  variant="h6" 
                  component="div"
                  color={totalGainLoss >= 0 ? "success.main" : "error.main"}
                >
                  {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lucro/Prejuízo total
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Sales Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Vendas recentes
          </Typography>
          
          {sales.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Ainda não há vendas registadas. Registe a primeira venda para acompanhar o desempenho da carteira.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Ação</TableCell>
                    <TableCell align="right">Quantidade</TableCell>
                    <TableCell align="right">Preço por unidade</TableCell>
                    <TableCell align="right">Câmbio</TableCell>
                    <TableCell align="right">Valor total</TableCell>
                    <TableCell align="right">Lucro/Prejuízo</TableCell>
                    <TableCell align="center">Detalhes</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.map((sale) => {
                    const isExpanded = expandedSales.has(sale.publicId);
                    const purchaseDetails = getPurchaseDetails(sale);
                    
                    return (
                      <React.Fragment key={sale.publicId}>
                        <TableRow>
                          <TableCell>{formatDate(sale.date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={sale.stockSymbol}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{isCryptoBySymbol(sale.stockSymbol) ? formatCryptoQuantity(sale.quantity) : formatQuantity(sale.quantity)}</TableCell>
                          <TableCell align="right">{formatCurrency(sale.pricePerShare)}</TableCell>
                            <TableCell align="right">{isCryptoBySymbol(sale.stockSymbol) ? formatCryptoQuantity(sale.quantity) : formatQuantity(sale.quantity)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main" fontWeight="bold">
                              {formatCurrency(sale.totalAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={sale.gainLoss >= 0 ? "success.main" : "error.main"}
                              fontWeight="bold"
                            >
                              {sale.gainLoss >= 0 ? '+' : ''}{formatCurrency(sale.gainLoss)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {sale.salePurchases && sale.salePurchases.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => toggleSaleExpansion(sale.publicId)}
                                color="primary"
                              >
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditSale(sale)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSale(sale)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable purchase details row */}
                        {sale.salePurchases && sale.salePurchases.length > 0 && (
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  <Typography variant="h6" gutterBottom component="div">
                                    Detalhe das compras
                                  </Typography>
                                  <Table size="small" aria-label="detalhes das compras">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Data da compra</TableCell>
                                        <TableCell align="right">Quantidade vendida</TableCell>
                                        <TableCell align="right">Preço de compra</TableCell>
                                        <TableCell align="right">Preço de venda</TableCell>
                                        <TableCell align="right">Lucro/Prejuízo</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {purchaseDetails.map((detail, index) => (
                                        <TableRow key={index}>
                                          <TableCell>
                                            {detail.purchaseDate ? formatDate(detail.purchaseDate) : 'Desconhecida'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {isCryptoBySymbol(sale.stockSymbol) ? formatCryptoQuantity(detail.quantitySold) : formatQuantity(detail.quantitySold)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {detail.purchasePrice ? (isCryptoBySymbol(sale.stockSymbol) ? formatCryptoQuantity(detail.purchasePrice) : formatQuantity(detail.purchasePrice)) : 'N/D'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {isCryptoBySymbol(sale.stockSymbol) ? formatCryptoQuantity(sale.pricePerShare) : formatQuantity(sale.pricePerShare)}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography 
                                              variant="body2" 
                                              color={detail.gainLoss >= 0 ? "success.main" : "error.main"}
                                              fontWeight="bold"
                                            >
                                              {detail.gainLoss >= 0 ? '+' : ''}{formatCurrency(detail.gainLoss)}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Sale Form Dialog */}
      <SaleForm
        open={saleFormOpen}
        initialValues={editingSale ? {
          date: editingSale.date.split('T')[0], // Convert to YYYY-MM-DD format
          quantity: editingSale.quantity,
          pricePerShare: editingSale.pricePerShare,
        } : undefined}
        onClose={() => {
          setSaleFormOpen(false);
          setEditingSale(null);
          setSelectedStock('');
        }}
        onSubmit={handleSaleFormSubmit}
        availableShares={selectedStock ? getAvailableShares(selectedStock) : editingSale ? getAvailableShares(editingSale.stockSymbol) : undefined}
        isCrypto={(() => {
          const symbol = selectedStock || editingSale?.stockSymbol || '';
          if (!symbol) return false;
          const s = stocks.find(x => x.symbol === symbol);
          return s?.investmentType === 5; // InvestmentType.Cryptocurrency
        })()}
      />

      {/* Stock Selection Dialog */}
      <StockSelectionDialog
        open={stockSelectionOpen}
        stocks={stocks}
        selectedStock={selectedStock}
        onStockChange={setSelectedStock}
        onClose={() => {
          setStockSelectionOpen(false);
          setSelectedStock('');
        }}
        onConfirm={handleStockSelected}
        getAvailableShares={getAvailableShares}
      />
    </Box>
  );
}
